import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Ban, Check, X, RefreshCcw, Clock, DollarSign } from "lucide-react";

const STATUS_META = {
  requested: { label: "Requested", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  refunded: { label: "Refunded", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  denied: { label: "Declined", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

const STATUS_FILTERS = ["requested", "approved", "refunded", "denied"];

export default function AdminCancellationsTab() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("requested");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/cancellations?status=${status}`)
      .then(r => setRows(r.data))
      .catch(() => setRows([]));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (orderId, action) => {
    const note = window.prompt(action === "approve"
      ? "Optional ops note (visible to the customer). Approving will trigger a Stripe refund."
      : "Optional decline reason (visible to the customer):");
    if (note === null) return;  // cancel pressed
    setBusy(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/cancel/${action}`, { ops_note: note || null });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-cancellations-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-2xl">Cancellations</h2>
        <span className="text-xs text-[var(--ff-text-muted)] ml-2">Approve to refund via Stripe. Refunded state auto-set by webhook.</span>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="cancel-filter-bar">
        {STATUS_FILTERS.map(s => {
          const active = status === s;
          return (
            <button key={s} onClick={() => setStatus(s)} data-testid={`cancel-filter-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${active ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white text-[var(--ff-text-secondary)] border-stone-300 hover:border-[var(--ff-primary)]"}`}>
              {STATUS_META[s].label}
            </button>
          );
        })}
        <button onClick={load} className="ml-auto text-xs inline-flex items-center gap-1 text-[var(--ff-primary)] hover:underline" data-testid="cancel-refresh">
          <RefreshCcw size={12}/> Refresh
        </button>
      </div>

      {rows === null && <p className="mt-8 text-[var(--ff-text-muted)]" data-testid="cancel-loading">Loading…</p>}
      {rows?.length === 0 && (
        <div className="mt-8 card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="cancel-empty">
          No orders in this bucket.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {rows?.map(o => {
          const cx = o.cancellation || {};
          const meta = STATUS_META[cx.status] || STATUS_META.requested;
          const itemSummary = (o.items || []).map(i => `${i.name} ×${i.qty}`).join(", ");
          return (
            <div key={o.id} className="card-flat p-5" data-testid={`cancel-row-${o.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-mono text-xs text-[var(--ff-text-muted)]">#{o.id.slice(0, 12)}</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`}>
                      <Ban size={11}/> {meta.label}
                    </span>
                    <span className="text-xs text-[var(--ff-text-muted)] inline-flex items-center gap-1">
                      <Clock size={11}/> {cx.requested_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  <p className="font-display text-lg mt-2">{itemSummary}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] mt-1">${(o.total || 0).toFixed(2)} · fulfillment: {o.fulfillment_status}</p>
                  {cx.reason && <p className="text-sm mt-3 text-[var(--ff-text-secondary)]"><strong>Customer:</strong> {cx.reason}</p>}
                  {cx.ops_note && <p className="text-xs mt-2 text-[var(--ff-text-muted)]"><strong>Ops:</strong> {cx.ops_note}</p>}
                  {cx.stripe_refund_id && (
                    <p className="text-xs mt-2 text-emerald-800 inline-flex items-center gap-1">
                      <DollarSign size={11}/> Refund: <span className="font-mono">{cx.stripe_refund_id}</span>
                    </p>
                  )}
                  {cx.refund_error && (
                    <p className="text-xs mt-2 text-rose-700">Refund error: {cx.refund_error}</p>
                  )}
                </div>
                {cx.status === "requested" && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => decide(o.id, "approve")}
                      disabled={busy === o.id}
                      className="bg-[var(--ff-primary)] hover:bg-[var(--ff-primary-dark)] disabled:opacity-60 text-white px-3 py-2 rounded font-semibold text-xs inline-flex items-center gap-1"
                      data-testid={`cancel-approve-${o.id}`}
                    >
                      <Check size={12}/> Approve & refund
                    </button>
                    <button
                      onClick={() => decide(o.id, "deny")}
                      disabled={busy === o.id}
                      className="border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60 px-3 py-2 rounded font-semibold text-xs inline-flex items-center gap-1"
                      data-testid={`cancel-deny-${o.id}`}
                    >
                      <X size={12}/> Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
